import { useEffect, useState } from "react";
import AuthService from "../../../../services/AuthService";
import UserService from "../../../../services/UserService";
import UtilService from "../../../../services/UtilService";
import { isStandardPayPlanForUser, PAY_PLAN_APP } from "../../../../services/PayPlanUtil";
import {
    getSlidingStoryTitle,
    pickSlidingOriginalImageUrl,
    pickSlidingPlayImageUrl,
} from "../../../../pages/browse-sliding/slidingBrowseUtils";
import { isConfigurationLocked } from "./subscriptionManager";
import { QUICK_PLAY_GRID } from "../routes";

/** Free tier: 4×4 + Easy only (see `subscriptionManager.js`). */
export const FREE_TIER_GRID_SIZE = QUICK_PLAY_GRID;
export const FREE_TIER_DIFFICULTIES = ["easy"];
export const FREE_TIER_SIGNUP_TOOLTIP = "Premium subscription required";
/** @deprecated Use FREE_TIER_SIGNUP_TOOLTIP */
export const FREE_TIER_6X6_TOOLTIP = FREE_TIER_SIGNUP_TOOLTIP;

/** Visible to XCTest once the home / choose-grid screen is ready (Fastlane snapshots). */
export const SNAPSHOT_MAIN_READY_LABEL = "PUZZLE_SNAPSHOT_MAIN_READY";

/** Visible to XCTest once the library list is loaded (Fastlane snapshots). */
export const SNAPSHOT_LIBRARY_READY_LABEL = "PUZZLE_SNAPSHOT_LIBRARY_READY";

/** XCTest marker after a library category filter is applied (`PUZZLE_SNAPSHOT_LIBRARY_TAG_{slug}_READY`). */
export function snapshotLibraryTagReadyLabel(tag) {
    if (tag == null || typeof tag !== "string") return null;
    const slug = tag.trim().toLowerCase();
    if (!slug) return null;
    return `PUZZLE_SNAPSHOT_LIBRARY_TAG_${slug}_READY`;
}

/** Visible to XCTest once the play grid shell is mounted (Fastlane snapshots). */
export const SNAPSHOT_PLAY_READY_LABEL = "PUZZLE_SNAPSHOT_PLAY_READY";

/** Visible to XCTest once an App Store screenshot scene is ready (`PUZZLE_SNAPSHOT_SCENE_{slug}_READY`). */
export function snapshotSceneReadyLabel(slug) {
    const s = String(slug ?? "").trim();
    if (!s) return null;
    return `PUZZLE_SNAPSHOT_SCENE_${s}_READY`;
}

/** Native accessibility id for screenshot scene `{slug}` (01–10). */
export function snapshotSceneNativeId(slug) {
    const s = String(slug ?? "").trim();
    if (!s) return null;
    return `SnapshotScene_${s}`;
}

/** Posts to iOS when an App Store screenshot scene has loaded its board. */
export function notifyNativeSnapshotSceneReady(slug) {
    const s = String(slug ?? "").trim();
    if (!s) return;
    postNativeSnapshotMessage({ ready: true, kind: "screenshot", slug: s });
}

/** Native accessibility ids set by `PuzzleBridgeViewController` (reliable for XCUI). */
export const NATIVE_SNAPSHOT_MAIN_READY_ID = "SnapshotMainReady";
export const NATIVE_SNAPSHOT_LIBRARY_READY_ID = "SnapshotLibraryReady";
export const NATIVE_SNAPSHOT_PLAY_READY_ID = "SnapshotPlayReady";
/** @deprecated Use NATIVE_SNAPSHOT_PLAY_READY_ID */
export const NATIVE_SNAPSHOT_READY_ID = NATIVE_SNAPSHOT_PLAY_READY_ID;

function postNativeSnapshotMessage(payload) {
    if (!isSnapshotUiTesting()) return;
    try {
        window.webkit?.messageHandlers?.puzzleSnapshot?.postMessage?.(payload);
    } catch {
        /* ignore */
    }
}

/** Posts to iOS when the home choose-grid screen is ready. */
export function notifyNativeSnapshotMainReady() {
    postNativeSnapshotMessage({ ready: true, kind: "main" });
}

/** Posts to iOS when the library browse list has real puzzle rows. */
export function notifyNativeSnapshotLibraryReady() {
    postNativeSnapshotMessage({ ready: true, kind: "library" });
}

/** Posts to iOS when a library category filter is applied (native marker for XCTest). */
export function notifyNativeSnapshotLibraryTagReady(tag) {
    const slug = typeof tag === "string" ? tag.trim().toLowerCase() : "";
    if (!slug) return;
    postNativeSnapshotMessage({ ready: true, kind: "library-tag", tag: slug });
}

/** Posts to iOS when the play board shows a published puzzle (not the demo grid). */
export function notifyNativeSnapshotPlayReady() {
    postNativeSnapshotMessage({ ready: true, kind: "play" });
}

/** True when URL is the bundled demo board (`public/icon-only.png`). */
export function isDemoSlidingImageUrl(url) {
    if (!url || typeof url !== "string") return true;
    return url.includes("sliding-logo-board");
}

/** Fastlane / XCTest snapshot bootstrap (`PuzzleBridgeViewController`). */
export function isSnapshotUiTesting() {
    try {
        if (window.__PUZZLE_UI_TESTING__ === true) {
            return true;
        }
        const path = window.__PUZZLE_SNAPSHOT_PATH__;
        return typeof path === "string" && path.startsWith("/");
    } catch {
        return false;
    }
}

export function getSnapshotSlidingId() {
    if (!isSnapshotUiTesting()) return null;
    try {
        const id = window.__PUZZLE_SNAPSHOT_SLIDING_ID__;
        return typeof id === "string" && id.trim() ? id.trim() : null;
    } catch {
        return null;
    }
}

/** Deep-link target injected before the SPA boots (`-PuzzleSnapshotPath`). */
export function getSnapshotPath() {
    if (!isSnapshotUiTesting()) return null;
    try {
        const p = window.__PUZZLE_SNAPSHOT_PATH__;
        return typeof p === "string" && p.startsWith("/") ? p : null;
    } catch {
        return null;
    }
}

/** Preloaded play payload injected by `PuzzleBridgeViewController` for snapshot play (no JWT). */
export function getSnapshotPlayBootstrap() {
    if (!isSnapshotUiTesting()) return null;
    try {
        const raw = window.__PUZZLE_SNAPSHOT_PLAY_STATE__;
        if (!raw || typeof raw !== "object") return null;
        return raw;
    } catch {
        return null;
    }
}

/** Offline play defaults for snapshots (grid renders without waiting on API). */
export function getSnapshotPlayDefaults() {
    if (!isSnapshotUiTesting()) return null;
    try {
        const raw = window.__PUZZLE_SNAPSHOT_PLAY__;
        if (!raw || typeof raw !== "object") {
            return { gridSize: FREE_TIER_GRID_SIZE, difficulty: "easy" };
        }
        return {
            gridSize: Number(raw.gridSize) || FREE_TIER_GRID_SIZE,
            difficulty: String(raw.difficulty || "easy").toLowerCase(),
        };
    } catch {
        return { gridSize: FREE_TIER_GRID_SIZE, difficulty: "easy" };
    }
}

/** @param {{ isAuthenticated?: boolean, isPremium?: boolean, hasStandardPlan?: boolean }} access */
export function isFreeTierPlayAccess({ isAuthenticated, isPremium, hasStandardPlan }) {
    const subscribed = hasStandardPlan ?? isPremium;
    return !subscribed;
}

export function canFreeTierPlayGridDifficulty(gridSize, difficulty) {
    return !isConfigurationLocked(gridSize, difficulty, false);
}

export function playAccessDeniedMessage(gridSize, difficulty) {
    const gs = Number(gridSize);
    const d = String(difficulty || "").toLowerCase();
    if (gs !== FREE_TIER_GRID_SIZE) {
        return "6×6 and other grid sizes require a Premium subscription.";
    }
    if (d === "medium" || d === "hard") {
        return "Medium and Hard difficulty require a Premium subscription.";
    }
    return "This puzzle mode requires a Premium subscription.";
}

/** True when signed-in users should call `/home/sliding-play/*` (start, progress, my puzzles). */
export function shouldRecordPlayHistory(access) {
    return Boolean(access && !access.loading && access.isAuthenticated);
}

/**
 * Cloud STANDARD/PREMIUM for Sliding Puzzle Quest (`payPlans.sliding_puzzle_quest`).
 * @param {object | null | undefined} cloudUser
 */
export function hasStandardPlanFromCloudUser(cloudUser) {
    if (!cloudUser) {
        return false;
    }
    if (cloudUser.userRole === "ADMIN") {
        return true;
    }
    return isStandardPayPlanForUser(cloudUser, PAY_PLAN_APP.SLIDING_PUZZLE_QUEST);
}

/**
 * @deprecated Prefer {@link hasStandardPlanFromCloudUser} with full cloudUser.
 * @param {string|undefined} payPlan
 * @param {string|undefined} userRole
 */
export function hasStandardPlanFromCloud(payPlan, userRole) {
    return UtilService.isStandardPayPlan(payPlan) || userRole === "ADMIN";
}

/**
 * @returns {Promise<{ isAuthenticated: boolean, isPremium: boolean, hasStandardPlan: boolean, freeTier: boolean }>}
 * `isPremium` is legacy naming — true when cloud `payPlan` is Standard (not a separate Premium tier).
 */
export async function resolveMobileSlidingAccess() {
    if (isSnapshotUiTesting()) {
        return {
            isAuthenticated: false,
            isPremium: true,
            hasStandardPlan: true,
            freeTier: false,
        };
    }
    let isAuthenticated = false;
    let hasStandardPlan = false;
    try {
        isAuthenticated = await AuthService.isAuthenticated();
    } catch {
        isAuthenticated = false;
    }
    if (isAuthenticated) {
        try {
            const userInfo = await UserService.getUserInfo();
            hasStandardPlan = hasStandardPlanFromCloudUser(userInfo?.cloudUser);
        } catch {
            // Cognito session is valid; cloud profile fetch failed — still allow play API sync.
            hasStandardPlan = false;
        }
    }
    const freeTier = isFreeTierPlayAccess({ isAuthenticated, hasStandardPlan });
    return {
        isAuthenticated,
        hasStandardPlan,
        isPremium: hasStandardPlan,
        freeTier,
    };
}

export function useMobileSlidingAccess() {
    const [access, setAccess] = useState({
        loading: true,
        isAuthenticated: false,
        isPremium: false,
        hasStandardPlan: false,
        freeTier: true,
    });

    useEffect(() => {
        let cancelled = false;
        resolveMobileSlidingAccess()
            .then((next) => {
                if (!cancelled) {
                    setAccess({ ...next, loading: false });
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setAccess({
                        loading: false,
                        isAuthenticated: false,
                        isPremium: false,
                        hasStandardPlan: false,
                        freeTier: true,
                    });
                }
            });
        const onAuth = () => {
            resolveMobileSlidingAccess().then((next) => {
                if (!cancelled) {
                    setAccess({ ...next, loading: false });
                }
            });
        };
        window.addEventListener("puzzleAuthChanged", onAuth);
        return () => {
            cancelled = true;
            window.removeEventListener("puzzleAuthChanged", onAuth);
        };
    }, []);

    return access;
}

/** Local play state (slidingId optional — no API progress / my-puzzles history when omitted). */
export function buildLocalPlayNavigateState({
    slidingId,
    gridSize,
    difficulty,
    story,
    storyTitle,
    imageUrl,
    previewImageUrl,
    layout,
    resume,
}) {
    const title = storyTitle || getSlidingStoryTitle(story) || undefined;
    const sid = typeof slidingId === "string" ? slidingId.trim() : "";
    return {
        gridSize: Number(gridSize) || FREE_TIER_GRID_SIZE,
        difficulty: String(difficulty || "easy").toLowerCase(),
        imageUrl: imageUrl || pickSlidingPlayImageUrl(story) || undefined,
        previewImageUrl: previewImageUrl ?? pickSlidingOriginalImageUrl(story) ?? undefined,
        storyTitle: title,
        ...(sid ? { slidingId: sid } : {}),
        ...(Array.isArray(layout) ? { layout, resume: Boolean(resume) } : {}),
    };
}
